import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import { apiFetch, ApiError } from '../../config/api'
import { ALL_CATALOG_CATEGORY_KEYS, CatalogCategoryKey } from '../../catalog/categoryKeys'
import { useCatalog } from '../../contexts/CatalogContext'
import { useProjects } from '../../contexts/ProjectsContext'
import Button from '../ui/Button'
import PageLoader from '../ui/PageLoader'
import SingleSelect from '../ui/SingleSelect'
import ConfirmDialog from '../ui/ConfirmDialog'

type AdminRow = {
  id: number
  category_key: string
  label: string
  sort_order: number
  is_active: number
  project_id: string | null
}

function formatCatalogCategoryKey(key: string): string {
  return key
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ')
}

const PROJECT_SCOPED_KEYS = new Set<CatalogCategoryKey>([
  'base_heat_number',
  'floor_weld_id',
  'project_client_spec'
])

function isProjectScopedCategory(key: CatalogCategoryKey): boolean {
  return PROJECT_SCOPED_KEYS.has(key)
}

const TABLE_FILTER_ALL = '__all__'
const TABLE_FILTER_LEGACY = '__legacy__'

export default function CatalogAdminPanel() {
  const { refresh: refreshCatalog } = useCatalog()
  const { projects } = useProjects()
  const [categoryKey, setCategoryKey] = useState<CatalogCategoryKey>(ALL_CATALOG_CATEGORY_KEYS[0])
  const [rows, setRows] = useState<AdminRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const [composerOpen, setComposerOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [addScopeProjectId, setAddScopeProjectId] = useState('')
  const [tableFilter, setTableFilter] = useState<string>(TABLE_FILTER_ALL)
  const addInputRef = useRef<HTMLInputElement | null>(null)
  const activeRows = rows.filter((row) => row.is_active === 1)

  const projectNameById = useCallback(
    (id: string | null) => {
      if (!id) return 'Unscoped (legacy)'
      return projects.find((p) => p.id === id)?.name || id
    },
    [projects]
  )

  const tableFilterProjectIds = useMemo(() => {
    if (!isProjectScopedCategory(categoryKey)) return [] as string[]
    const ids = new Set<string>()
    activeRows.forEach((r) => {
      if (r.project_id) ids.add(r.project_id)
    })
    projects.forEach((p) => ids.add(p.id))
    return Array.from(ids).sort((a, b) => projectNameById(a).localeCompare(projectNameById(b)))
  }, [categoryKey, activeRows, projects, projectNameById])

  const filteredActiveRows = useMemo(() => {
    if (!isProjectScopedCategory(categoryKey)) return activeRows
    if (tableFilter === TABLE_FILTER_ALL) return activeRows
    if (tableFilter === TABLE_FILTER_LEGACY) {
      return activeRows.filter((r) => !r.project_id)
    }
    return activeRows.filter((r) => r.project_id === tableFilter)
  }, [activeRows, categoryKey, tableFilter])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<AdminRow[]>(
        `/api/catalog/admin?categoryKey=${encodeURIComponent(categoryKey)}`
      )
      setRows(data)
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : 'Failed to load catalog'
      setError(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [categoryKey])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setComposerOpen(false)
    setNewLabel('')
    setAddScopeProjectId('')
    setTableFilter(TABLE_FILTER_ALL)
  }, [categoryKey])

  useEffect(() => {
    if (composerOpen) {
      addInputRef.current?.focus()
    }
  }, [composerOpen])

  const afterMutation = useCallback(async () => {
    await load()
    await refreshCatalog()
  }, [load, refreshCatalog])

  const startEdit = (row: AdminRow) => {
    setComposerOpen(false)
    setNewLabel('')
    setEditingId(row.id)
    setEditLabel(row.label)
  }

  const saveEdit = async () => {
    if (editingId == null) return
    if (!editLabel.trim()) return
    try {
      await apiFetch(`/api/catalog/${editingId}`, {
        method: 'PATCH',
        body: JSON.stringify({ label: editLabel.trim() })
      })
      setEditingId(null)
      await afterMutation()
      toast.success('Label updated.')
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : 'Save failed'
      setError(msg)
      toast.error(msg)
    }
  }

  const performDelete = async (id: number) => {
    setDeleteBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/catalog/${id}`, { method: 'DELETE' })
      await afterMutation()
      toast.success('Catalog entry deleted.')
      setDeleteTargetId(null)
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : 'Delete failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setDeleteBusy(false)
    }
  }

  const submitNewLabel = async () => {
    const trimmed = newLabel.trim()
    if (!trimmed) return
    if (isProjectScopedCategory(categoryKey)) {
      if (!addScopeProjectId.trim()) {
        toast.error('Select a project for this catalog entry.')
        return
      }
    }
    setAdding(true)
    setError(null)
    try {
      await apiFetch(`/api/catalog`, {
        method: 'POST',
        body: JSON.stringify({
          categoryKey,
          label: trimmed,
          ...(isProjectScopedCategory(categoryKey) ? { projectId: addScopeProjectId.trim() } : {})
        })
      })
      setNewLabel('')
      setComposerOpen(false)
      setAddScopeProjectId('')
      await afterMutation()
      toast.success('Label added.')
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.message : 'Add failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setAdding(false)
    }
  }

  const cancelComposer = () => {
    setComposerOpen(false)
    setNewLabel('')
    setAddScopeProjectId('')
  }

  const filterRowOptions = useMemo(
    () => [TABLE_FILTER_ALL, TABLE_FILTER_LEGACY, ...tableFilterProjectIds],
    [tableFilterProjectIds]
  )

  return (
    <div className="space-y-3">
      <ConfirmDialog
        open={deleteTargetId !== null}
        title="Delete catalog entry?"
        description="This option will be removed from all dropdowns that use this catalog category. This cannot be undone from the admin panel."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        confirmBusy={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteTargetId(null)
        }}
        onConfirm={async () => {
          if (deleteTargetId != null) await performDelete(deleteTargetId)
        }}
      />
      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50/80 p-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1 sm:max-w-md">
          <label className="text-xs font-medium text-gray-500">Category</label>
          <SingleSelect
            className="mt-1"
            value={categoryKey}
            options={[...ALL_CATALOG_CATEGORY_KEYS]}
            onChange={(next) => setCategoryKey(next as CatalogCategoryKey)}
            placeholder="Select category"
            getOptionLabel={formatCatalogCategoryKey}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={() => {
              setEditingId(null)
              setComposerOpen((o) => !o)
              if (composerOpen) setNewLabel('')
            }}
            variant={composerOpen ? 'secondary' : 'primary'}
          >
            {composerOpen ? 'Cancel add' : 'Add label'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {isProjectScopedCategory(categoryKey) && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <div className="min-w-[14rem] flex-1 sm:max-w-xs">
            <label className="text-xs font-medium text-gray-500">Show rows</label>
            <SingleSelect
              className="mt-1"
              value={tableFilter}
              options={filterRowOptions}
              onChange={setTableFilter}
              getOptionLabel={(o) =>
                o === TABLE_FILTER_ALL ? 'All' : o === TABLE_FILTER_LEGACY ? 'Unscoped (legacy)' : projectNameById(o)
              }
              placeholder="Filter"
            />
          </div>
        </div>
      )}

      {error && <p className="text-sm text-error-600">{error}</p>}
      {loading ? (
        <PageLoader label="Loading catalog…" compact />
      ) : (
        <>
          {composerOpen && (
            <div
              className="rounded-lg border border-primary-200 bg-gradient-to-b from-primary-50/90 to-white p-3 shadow-sm ring-1 ring-primary-100/60"
              role="region"
              aria-label="Add new catalog label"
            >
              <label htmlFor="catalog-new-label" className="text-xs font-semibold text-primary-900">
                New label
              </label>
              <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  id="catalog-new-label"
                  ref={addInputRef}
                  className="input-primary min-h-[2.5rem] w-full flex-1 text-sm sm:min-w-[18rem]"
                  placeholder="Type the option as it should appear in dropdowns…"
                  value={newLabel}
                  disabled={adding}
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void submitNewLabel()
                    }
                    if (e.key === 'Escape') cancelComposer()
                  }}
                />
                <div className="flex shrink-0 justify-end gap-2 sm:justify-start">
                  <Button
                    type="button"
                    className="h-9 min-w-[4.5rem] px-3 text-xs sm:text-sm"
                    disabled={
                      adding ||
                      !newLabel.trim() ||
                      (isProjectScopedCategory(categoryKey) && !addScopeProjectId.trim())
                    }
                    onClick={() => void submitNewLabel()}
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-9 px-3 text-xs sm:text-sm"
                    disabled={adding}
                    onClick={cancelComposer}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
              {isProjectScopedCategory(categoryKey) && (
                <div className="mt-3 rounded-md border border-primary-100 bg-white/70 p-2">
                  <label className="text-xs font-semibold text-primary-900">Project</label>
                  <SingleSelect
                    className="mt-1 max-w-md"
                    value={addScopeProjectId}
                    options={projects.map((p) => p.id)}
                    onChange={setAddScopeProjectId}
                    getOptionLabel={(id) => projects.find((p) => p.id === id)?.name || id}
                    placeholder="Select project"
                  />
                  <p className="mt-1 text-[11px] text-gray-600">
                    Heat numbers, client welding specs, and floor weld IDs are stored per project and only appear in
                    that project&apos;s forms and dropdowns.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeRows.length === 0 && !composerOpen ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-4 py-10 text-center">
              <p className="text-sm font-medium text-gray-800">No labels in this category</p>
              <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-gray-500">
                Options you add here appear in WPS form dropdowns for this field. Use &quot;Add label&quot; above to create
                the first entry.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <div
                className="divide-y divide-gray-200"
                role="table"
                aria-label="Catalog labels for the selected category"
              >
                <div
                  className="flex items-center gap-2 bg-gray-50 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500"
                  role="row"
                >
                  <span className="w-10 shrink-0 text-center" role="columnheader">
                    #
                  </span>
                  {isProjectScopedCategory(categoryKey) ? (
                    <>
                      <span className="w-36 shrink-0 pl-0.5" role="columnheader">
                        Project
                      </span>
                      <span className="min-w-0 flex-1 pl-0.5" role="columnheader">
                        Value
                      </span>
                    </>
                  ) : (
                    <span className="min-w-0 flex-1 pl-0.5" role="columnheader">
                      Label
                    </span>
                  )}
                  <span className="shrink-0 w-24 pl-2 text-right" role="columnheader">
                    Actions
                  </span>
                </div>
                {activeRows.length === 0 && composerOpen && (
                  <div className="bg-gray-50/50 px-3 py-3 text-center text-xs text-gray-500" role="row">
                    No labels yet — save one above and it will show in this list.
                  </div>
                )}
                {filteredActiveRows.length === 0 && activeRows.length > 0 && (
                  <div className="bg-amber-50/50 px-3 py-3 text-center text-xs text-gray-600" role="row">
                    No rows match this filter.
                  </div>
                )}
                {filteredActiveRows.map((row, index) => (
                  <div
                    key={row.id}
                    className="flex items-center gap-2 px-2 py-1.5 transition-colors hover:bg-gray-50/80"
                    role="row"
                  >
                    <span
                      className="w-10 shrink-0 text-center text-xs tabular-nums text-gray-500"
                      aria-hidden
                    >
                      {(index + 1).toString()}
                    </span>
                    <div className="min-w-0 flex-1" role="cell">
                      {editingId === row.id ? (
                        <input
                          className="input-primary w-full min-w-0 text-sm"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void saveEdit()
                            if (e.key === 'Escape') setEditingId(null)
                          }}
                        />
                      ) : isProjectScopedCategory(categoryKey) ? (
                        <div className="flex min-w-0 gap-2">
                          <span className="w-36 shrink-0 truncate text-xs text-gray-500" title={projectNameById(row.project_id)}>
                            {projectNameById(row.project_id)}
                          </span>
                          <span className="min-w-0 truncate text-sm text-gray-900" title={row.label}>
                            {row.label}
                          </span>
                        </div>
                      ) : (
                        <span className="block truncate text-sm text-gray-900" title={row.label}>
                          {row.label}
                        </span>
                      )}
                    </div>
                    <div className="flex w-24 shrink-0 items-center justify-end gap-1 pl-2" role="cell">
                      {editingId === row.id ? (
                        <>
                          <Button type="button" className="h-7 px-2 text-xs" onClick={() => void saveEdit()}>
                            Save
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            className="h-7 px-2 text-xs"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-gray-200 bg-white text-primary-600 shadow-sm transition hover:border-primary-200 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                            aria-label={`Edit ${row.label}`}
                            onClick={() => startEdit(row)}
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md border border-gray-200 bg-white text-error-600 shadow-sm transition hover:border-error-200 hover:bg-error-50 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-1"
                            aria-label={`Delete ${row.label}`}
                            onClick={() => setDeleteTargetId(row.id)}
                          >
                            <TrashIcon className="h-3.5 w-3.5" aria-hidden />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
